'use client'

import * as React from 'react'
import Link from 'next/link'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { apiClient } from '@/lib/api-client'

/** One in-app notification, as /api/notifications/mine returns it. */
interface InboxItem {
  id: number
  subject: string
  body: string
  created_at: string | null
  read: boolean
}

const POLL_MS = 60_000
// An in-app path inside a message (an approval card, a requirement), shown as a link instead of text.
const APP_PATH = /(\/(?:flow\/card|pr|rfp|flow|direct\/po|vendor)\/[A-Za-z0-9_\-/.]+)/

function formatRelativeTime(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  const diffMins = Math.floor((Date.now() - date.getTime()) / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

function MessageBody({ text }: { text: string }) {
  const match = text.match(APP_PATH)
  if (!match || match.index === undefined) return <>{text}</>
  const before = text.slice(0, match.index).replace(/[:\s]+$/, '')
  const after = text.slice(match.index + match[1].length)
  const isCard = match[1].startsWith('/flow/card/')
  return (
    <>
      {before}{' '}
      <Link href={match[1]} className='font-semibold text-brand-navy underline underline-offset-2'>
        {isCard ? 'Open the approval card' : 'Open'}
      </Link>
      {after}
    </>
  )
}

function NotificationItem({ item, onRead }: { item: InboxItem; onRead: (id: number) => void }) {
  return (
    <div
      className={cn('flex gap-3 rounded-xl p-3 transition-colors', item.read ? 'opacity-70' : 'bg-primary-500/5')}
      onClick={() => !item.read && onRead(item.id)}
    >
      <div className='flex-shrink-0 rounded-lg bg-blue-500/10 p-2'>
        <Icons.info className='h-4 w-4 text-blue-500' strokeWidth={1.5} />
      </div>
      <div className='min-w-0 flex-1'>
        <div className='flex items-start justify-between gap-2'>
          <p className='text-sm font-medium text-text-primary'>{item.subject}</p>
          {!item.read && <span className='mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary-500' aria-label='Unread' />}
        </div>
        <p className='mt-0.5 text-xs text-text-secondary'>
          <MessageBody text={item.body} />
        </p>
        <p className='mt-1 text-[10px] text-text-secondary/60'>{formatRelativeTime(item.created_at)}</p>
      </div>
    </div>
  )
}

/** The header bell: messages sent to the signed-in person or their roles (approval cards, reminders, alerts). */
export function NotificationCenter() {
  const [items, setItems] = React.useState<InboxItem[]>([])

  const load = React.useCallback(async () => {
    try {
      setItems((await apiClient.get<{ items: InboxItem[] }>('/api/notifications/mine')).items)
    } catch {
      // The bell stays as it was; the next poll retries.
    }
  }, [])

  React.useEffect(() => {
    void load()
    const timer = window.setInterval(() => void load(), POLL_MS)
    return () => window.clearInterval(timer)
  }, [load])

  const markAsRead = async (id: number) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)))
    await apiClient.post(`/api/notifications/mine/${id}/read`).catch(() => undefined)
  }

  const markAllAsRead = async () => {
    const unread = items.filter((n) => !n.read)
    setItems((prev) => prev.map((n) => ({ ...n, read: true })))
    await Promise.all(unread.map((n) => apiClient.post(`/api/notifications/mine/${n.id}/read`).catch(() => undefined)))
  }

  const unreadCount = items.filter((n) => !n.read).length

  return (
    <Popover onOpenChange={(open) => open && void load()}>
      <PopoverTrigger asChild>
        <Button
          variant='ghost'
          size='icon'
          aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
          className='relative rounded-full text-text-secondary hover:text-text-primary'
        >
          <Icons.bell className='h-5 w-5' strokeWidth={1.5} />
          {unreadCount > 0 && (
            <span className='absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-white'>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-96 p-0'>
        <div className='flex items-center justify-between border-b border-border-default px-4 py-3'>
          <h3 className='font-display font-semibold text-text-primary'>Notifications</h3>
          {unreadCount > 0 && (
            <Button variant='link' size='small' onClick={() => void markAllAsRead()} className='h-auto p-0 text-xs text-primary-500'>
              Mark all as read
            </Button>
          )}
        </div>
        <div className='max-h-[360px] overflow-y-auto p-2'>
          {items.length === 0 ? (
            <div className='py-8 text-center'>
              <div className='mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-bg-subtle/50'>
                <Icons.checkCircle className='h-6 w-6 text-text-secondary' />
              </div>
              <p className='text-sm font-medium text-text-primary'>All caught up!</p>
              <p className='mt-1 text-xs text-text-secondary'>No new notifications</p>
            </div>
          ) : (
            <div className='space-y-1'>
              {items.map((item) => (
                <NotificationItem key={item.id} item={item} onRead={(id) => void markAsRead(id)} />
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
